/**
 * 微信内分享卡定制（wx.config + 自定义标题/图/描述）
 *
 * 工作流程：
 *   1. 仅在「微信内」执行（其它浏览器直接跳过）
 *   2. 动态加载微信 JS-SDK script
 *   3. 用当前 URL 向后端换 wx.config 签名参数
 *   4. wx.config 初始化后，调用 updateAppMessageShareData / updateTimelineShareData 设置分享内容
 *
 * 容错：
 *   - 非微信内：直接 return（不报错）
 *   - 后端 503（未配 AppID/AppSecret）：直接 return，降级用微信默认分享卡
 *   - JS-SDK 脚本加载失败 / wx.config error：吞掉，不影响业务
 *
 * 调用入口：在需要定制分享的页面 useEffect 里调一次 setupWechatShare()
 */

const WX_SDK_URL = 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js'

interface WxShareData {
  title: string
  desc: string
  link: string
  imgUrl: string
}

interface WxJsApi {
  config: (opts: any) => void
  ready: (cb: () => void) => void
  error: (cb: (e: unknown) => void) => void
  updateAppMessageShareData: (opts: WxShareData & { success?: () => void }) => void
  updateTimelineShareData: (opts: WxShareData & { success?: () => void }) => void
  onMenuShareAppMessage?: (opts: WxShareData) => void
  onMenuShareTimeline?: (opts: WxShareData) => void
}

declare global {
  interface Window {
    wx?: WxJsApi
  }
}

export function isInWeChat(): boolean {
  if (typeof navigator === 'undefined') return false
  return /micromessenger/i.test(navigator.userAgent)
}

let sdkLoadingPromise: Promise<void> | null = null

function loadWxSdk(): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'))
  if (window.wx) return Promise.resolve()
  if (sdkLoadingPromise) return sdkLoadingPromise
  sdkLoadingPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script')
    s.async = true
    s.src = WX_SDK_URL
    s.onload = () => resolve()
    s.onerror = () => reject(new Error('failed to load weixin jssdk'))
    document.head.appendChild(s)
  })
  return sdkLoadingPromise
}

async function fetchConfig(url: string): Promise<any | null> {
  try {
    const r = await fetch(`/api/h5/v1/wechat/jssdk-config?url=${encodeURIComponent(url)}`, {
      credentials: 'omit'
    })
    if (!r.ok) return null
    return await r.json()
  } catch {
    return null
  }
}

export interface WxShareOptions {
  title?: string
  desc?: string
  imgUrl?: string
  link?: string
}

const DEFAULT_SHARE: Required<Omit<WxShareOptions, 'link'>> = {
  title: '邹城中考志愿填报工具 · 方法星球',
  desc: '输入分数，3 秒得到邹城五校的录取概率区间与「冲 / 稳 / 保」建议。完全免费 · 数据本地计算 · 不收集隐私。',
  imgUrl: 'https://zhongkao.fangfa.online/logo_1.png'
}

/**
 * 在微信内启用自定义分享卡。
 * 在每个需要的页面 mount 时调一次（标题/描述可按页面定制）。
 *
 * 注意：微信要求 wx.config 的 url 必须是「未做任何 hash 修改的当前页面 URL」，
 *      而分享出去的 link 可以指向任意 URL。我们把 link 默认设为当前页 URL。
 */
export async function setupWechatShare(opts: WxShareOptions = {}): Promise<void> {
  if (!isInWeChat()) return
  if (typeof window === 'undefined') return

  // 微信签名时不能带 hash
  const signUrl = window.location.href.split('#')[0]
  const link = opts.link || window.location.href

  const cfg = await fetchConfig(signUrl)
  if (!cfg || !cfg.appId) return // 未配置 / 后端 503 / 网络失败 → 静默降级

  try {
    await loadWxSdk()
  } catch {
    return
  }
  const wx = window.wx
  if (!wx) return

  wx.config({
    debug: false,
    appId: cfg.appId,
    timestamp: cfg.timestamp,
    nonceStr: cfg.nonceStr,
    signature: cfg.signature,
    jsApiList: cfg.jsApiList || [
      'updateAppMessageShareData',
      'updateTimelineShareData'
    ]
  })

  const share: WxShareData = {
    title: opts.title || DEFAULT_SHARE.title,
    desc: opts.desc || DEFAULT_SHARE.desc,
    link,
    imgUrl: opts.imgUrl || DEFAULT_SHARE.imgUrl
  }

  wx.ready(() => {
    try {
      wx.updateAppMessageShareData(share)
      wx.updateTimelineShareData(share)
    } catch (_) {
      /* 静默 */
    }
  })
  wx.error(() => {
    /* config 失败：常见原因是 JS 安全域名未在公众号后台绑定；静默 */
  })
}
